import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiClient } from '@/lib/api'

interface AddTenantModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function AddTenantModal({ isOpen, onClose, onSuccess }: AddTenantModalProps) {
  const [displayName, setDisplayName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!displayName.trim()) {
      setError('Display Name is required')
      return
    }
    
    if (!subdomain.trim()) {
      setError('Subdomain is required')
      return
    }
    
    // Validate subdomain format
    const subdomainRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/
    if (subdomain.length < 3 || subdomain.length > 63 || !subdomainRegex.test(subdomain)) {
      setError('Subdomain must be 3-63 characters, lowercase letters, numbers, and hyphens only')
      return
    }

    setLoading(true)
    setError('')

    try {
      await apiClient.createTenant({
        display_name: displayName.trim(),
        subdomain: subdomain.trim().toLowerCase()
      })
      
      // Reset form
      setDisplayName('')
      setSubdomain('')
      
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setDisplayName('')
    setSubdomain('')
    setError('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription>
            Add a new tenant to the system. Choose a unique subdomain for tenant access.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Tenant Name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subdomain">Subdomain *</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="subdomain"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="mycompany"
                pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                minLength={3}
                maxLength={63}
                required
              />
              <span className="text-sm text-gray-500">.queuemein.com</span>
            </div>
            <p className="text-xs text-gray-500">
              3-63 characters, lowercase letters, numbers, and hyphens only. Cannot be changed after creation.
            </p>
          </div>
          
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}